import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TypeBadge from '../../components/TypeBadge';
import LanguageSelect from '../../components/LanguageSelect';
import api from '../../api/ApiClient';

const TYPE_FEATURES = {
  dataset:     { modality: true,  languages: true,  modelMeta: false, datasetMeta: true,  previewText: false },
  llm:         { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: true  },
  image_model: { modality: true,  languages: false, modelMeta: true,  datasetMeta: false, previewText: true  },
  audio_model: { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: true  },
  cv_model:    { modality: true,  languages: false, modelMeta: true,  datasetMeta: false, previewText: true  },
  embedding:   { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: false },
  other_model: { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: true  },
  notebook:    { modality: false, languages: true,  modelMeta: false, datasetMeta: false, previewText: false },
  pipeline:    { modality: false, languages: true,  modelMeta: false, datasetMeta: false, previewText: false },
};

const TYPES       = ['dataset','llm','image_model','audio_model','cv_model','embedding','other_model','notebook','pipeline'];
const MODALITIES  = ['text','image','audio','video','tabular','3d','multimodal','code','other'];
const CATEGORIES  = ['nlp','computer_vision','audio','tabular','finance','healthcare','science','retail','autonomous','robotics','gaming','other'];
const LICENSES    = ['MIT','CC-BY-4.0','CC-BY-NC-4.0','CC-BY-SA-4.0','Apache-2.0','GPL-3.0','Commercial','Custom'];
const FRAMEWORKS  = ['PyTorch','TensorFlow','JAX','ONNX','GGUF','Safetensors','Keras','scikit-learn','Other'];
const QUANTIZATIONS = ['none','fp32','fp16','bf16','int8','int4','Q4_K_M','Q5_K_M','Q8_0','Other'];
const COLLECTION_METHODS = ['scraped','synthetic','human-labeled','crowdsourced','purchased','open-source'];
const ANNOTATION_TYPES   = ['none','bounding_box','segmentation','sentiment','ner','classification','transcription','caption','other'];

const inp = {
  width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-sm)', boxSizing: 'border-box',
  border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', fontSize: 14, outline: 'none', fontFamily: 'inherit',
};

function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>
        {label}{hint && <span style={{ fontWeight: 400, color: '#64748B', marginLeft: 6 }}>— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '28px 0 14px', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>{children}</h3>;
}

const FILE_ICONS = { csv: '📊', tsv: '📊', json: '📋', jsonl: '📋', zip: '📦', gz: '📦', parquet: '📊', csv_gz: '📦', safetensors: '🧠', gguf: '🧠', pt: '🧠', bin: '🧠', pth: '🧠', mp4: '🎬', mp3: '🎵', wav: '🎵', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', pdf: '📄', ipynb: '📓', py: '🐍', default: '📄' };
const fileIcon = fmt => FILE_ICONS[(fmt || '').toLowerCase()] || FILE_ICONS.default;
const fmtSize = b => b > 1e9 ? `${(b/1e9).toFixed(1)} GB` : b > 1e6 ? `${(b/1e6).toFixed(1)} MB` : b > 1e3 ? `${(b/1e3).toFixed(0)} KB` : `${b} B`;

// ── DataPreview inside edit page ──────────────────────────────────────────────
function DataPreviewTable({ asset }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!asset?._id) return;
    api.previewAssetData(asset._id)
      .then(d => setData(d))
      .catch(() => setData({ success: false, columns: [], rows: [], files: [], message: 'Could not load preview.' }))
      .finally(() => setLoading(false));
  }, [asset?._id]);

  if (loading) return <div style={{ padding: '12px 0', color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>;
  if (!data) return null;

  const { columns, rows, files, message, source, zipContents } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Files list */}
      {files?.length > 0 && (
        <div style={{ background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)', padding: '4px 0' }}>
          {files.map((f, i) => (
            <div key={String(f._id || i)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: i < files.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontSize: 16 }}>{fileIcon(f.format)}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{f.label || 'Untitled'}</span>
                <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 8, fontFamily: 'Space Mono' }}>
                  {f.format?.toUpperCase()}{f.sizeBytes ? ` · ${fmtSize(f.sizeBytes)}` : ''}
                </span>
              </div>
              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: f.isPrivate ? '#FEF2F2' : '#F0FDF4', color: f.isPrivate ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                {f.isPrivate ? 'private' : 'public'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ZIP contents listing */}
      {zipContents?.length > 0 && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Contents of ZIP:</div>
          <div style={{ background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)', padding: '4px 0' }}>
            {zipContents.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderBottom: i < zipContents.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                <span>{fileIcon(f.name.split('.').pop())}</span>
                <span style={{ flex: 1, fontFamily: 'Space Mono', color: 'var(--text-2)' }}>{f.name}</span>
                <span style={{ color: 'var(--text-3)' }}>{f.size ? fmtSize(f.size) : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data table */}
      {columns?.length > 0 && rows?.length > 0 ? (
        <div>
          {source && <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontFamily: 'Space Mono' }}>from: {source}</div>}
          <div style={{ overflow: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {columns.map(c => (
                    <th key={c} style={{ padding: '7px 12px', textAlign: 'left', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', color: 'var(--text-3)', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'Space Mono' }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? 'var(--surface)' : 'var(--surface2)' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cell == null || cell === '' ? <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>—</span> : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        message && <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '4px 0' }}>{message}</div>
      )}
    </div>
  );
}

const bumpV = v => { const p = (v||'1.0.0').split('.').map(Number); p[0]+=1; return p.join('.'); };

// ── Main ──────────────────────────────────────────────────────────────────────
export default function EditAsset() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loadingAsset, setLoadingAsset] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [originalAsset, setOriginalAsset] = useState(null);
  const [releaseVersion, setReleaseVersion] = useState(false);
  const [changelog, setChangelog] = useState('');

  // Form state
  const [assetType, setAssetType]     = useState('');
  const [title, setTitle]             = useState('');
  const [shortDesc, setShortDesc]     = useState('');
  const [fullDesc, setFullDesc]       = useState('');
  const [category, setCategory]       = useState('');
  const [license, setLicense]         = useState('MIT');
  const [price, setPrice]             = useState('0');
  const [tags, setTags]               = useState([]);
  const [tagInput, setTagInput]       = useState('');
  const [languages, setLanguages]     = useState([]);
  const [dataModality, setDataModality] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [modelMeta, setModelMeta]     = useState({ architecture: '', parameters: '', framework: '', baseModel: '', taskType: '', quantization: '' });
  const [datasetMeta, setDatasetMeta] = useState({ rowCount: '', collectionMethod: '', annotationType: '' });

  // Files
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles]           = useState([]);
  const [addLinkMode, setAddLinkMode]     = useState(false);
  const [linkForm, setLinkForm]           = useState({ label: '', url: '', format: '', isPrivate: false });
  const [deletingFile, setDeletingFile]   = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');

  const features = TYPE_FEATURES[assetType] || {};

  // Load asset
  useEffect(() => {
    api.getAsset(id)
      .then(d => {
        const a = d.asset;
        setOriginalAsset(a);
        setAssetType(a.assetType || '');
        setTitle(a.title || '');
        setShortDesc(a.shortDescription || '');
        setFullDesc(a.fullDescription || '');
        setCategory(a.category || '');
        setLicense(a.license || 'MIT');
        setPrice(String(a.price ?? 0));
        setTags(a.tags || []);
        setLanguages(a.languages || []);
        setDataModality(a.dataModality || []);
        setPreviewImage(a.previewImage || '');
        setPreviewText(a.previewText || '');
        setModelMeta({ architecture: '', parameters: '', framework: '', baseModel: '', taskType: '', quantization: '', ...(a.modelMeta || {}) });
        setDatasetMeta({ rowCount: '', collectionMethod: '', annotationType: '', ...(a.datasetMeta || { rowCount: a.datasetMeta?.rowCount || '' }) });
        setExistingFiles(a.files || []);
      })
      .catch(() => navigate('/dashboard/assets'))
      .finally(() => setLoadingAsset(false));
  }, [id, navigate]);

  const addTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim().replace(',', '');
      if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
      setTagInput('');
    }
  };

  const stageFiles = (fileList) => {
    const arr = Array.from(fileList).map(file => ({
      id: Math.random().toString(36).slice(2),
      label: file.name.replace(/\.[^.]+$/, ''),
      file,
      format: file.name.split('.').pop().toLowerCase(),
      isPrivate: true,
    }));
    setNewFiles(prev => [...prev, ...arr]);
  };

  const addLink = () => {
    if (!linkForm.url) return;
    setNewFiles(prev => [...prev, {
      id: Math.random().toString(36).slice(2),
      label: linkForm.label || linkForm.url,
      url: linkForm.url,
      format: linkForm.format || 'url',
      isPrivate: linkForm.isPrivate,
      isLink: true,
    }]);
    setLinkForm({ label: '', url: '', format: '', isPrivate: false });
    setAddLinkMode(false);
  };

  const deleteExistingFile = async (fileId) => {
    if (!window.confirm('Remove this file?')) return;
    setDeletingFile(fileId);
    try {
      await api.deleteAssetFile(id, fileId);
      setExistingFiles(prev => prev.filter(f => String(f._id) !== fileId));
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to delete file');
    }
    setDeletingFile(null);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!title.trim()) { setError('Title is required'); return; }

    setSaving(true);
    try {
      const payload = {
        assetType,
        dataModality:     JSON.stringify(features.modality ? dataModality : []),
        languages:        JSON.stringify(features.languages ? languages : []),
        title:            title.trim(),
        shortDescription: shortDesc,
        fullDescription:  fullDesc,
        category,
        tags:             JSON.stringify(tags),
        price:            Number(price) || 0,
        license,
        previewImage,
        previewText:      features.previewText ? previewText : '',
        modelMeta:        JSON.stringify(features.modelMeta ? modelMeta : {}),
        datasetMeta:      JSON.stringify(features.datasetMeta ? { ...datasetMeta, rowCount: Number(datasetMeta.rowCount) } : {}),
      };

      await api.updateAsset(id, payload);

      // Upload new files
      for (let i = 0; i < newFiles.length; i++) {
        const f = newFiles[i];
        setUploadProgress(`Uploading ${i + 1}/${newFiles.length}…`);
        if (f.isLink) {
          await api.addAssetLink(id, { label: f.label, url: f.url, format: f.format, isPrivate: f.isPrivate });
        } else {
          const fd = new FormData();
          fd.append('file', f.file);
          fd.append('label', f.label || f.file.name);
          fd.append('isPrivate', String(f.isPrivate));
          await api.uploadAssetFile(id, fd);
        }
      }

      setNewFiles([]);
      setUploadProgress('');

      // Release new version if requested
      if (releaseVersion) {
        await api.createVersion(id, changelog);
        setReleaseVersion(false);
        setChangelog('');
        setSuccess('Saved and released as new version.');
      } else {
        setSuccess('Saved successfully.');
      }

      // Refresh asset
      const d = await api.getAsset(id);
      setOriginalAsset(d.asset);
      setExistingFiles(d.asset.files || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed');
    }
    setSaving(false);
    setUploadProgress('');
  };

  const handlePublish = async () => {
    setError('');
    setPublishing(true);
    try {
      await handleSave();
      await api.publishAsset(id);
      navigate('/dashboard/assets');
    } catch (err) {
      setError(err?.response?.data?.message || 'Publish failed');
    }
    setPublishing(false);
  };

  if (loadingAsset) {
    return <div style={{ padding: 32, color: 'var(--text-3)', fontSize: 14 }}>Loading…</div>;
  }

  const isPublished = originalAsset?.status === 'published';

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/dashboard/assets')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 20, padding: '0 4px', lineHeight: 1 }}>←</button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Asset</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <TypeBadge type={assetType} size="sm" />
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{originalAsset?.status}</span>
            <span style={{ fontSize: 11, fontFamily: 'Space Mono', color: 'var(--accent)', background: 'var(--accent-bg)', padding: '1px 7px', borderRadius: 4 }}>v{originalAsset?.currentVersion || '1.0.0'}</span>
            {isPublished && !originalAsset?.verifiedAt && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, color: '#92400E', background: '#FEF3C7' }}>unverified</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>{error}</div>
      )}
      {success && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--success-bg)', color: 'var(--success)', fontSize: 13, marginBottom: 16 }}>{success}</div>
      )}

      {/* ── Data Preview ─────────────────────────────────────────────────── */}
      <SectionTitle>Data Preview</SectionTitle>
      <DataPreviewTable asset={originalAsset} />

      {/* ── Basic Info ───────────────────────────────────────────────────── */}
      <SectionTitle>Basic Info</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Type">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TYPES.map(t => (
              <button key={t} onClick={() => setAssetType(t)} style={{
                padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                border: `1.5px solid ${assetType === t ? 'var(--accent)' : 'var(--border)'}`,
                background: assetType === t ? 'var(--accent-bg)' : 'var(--surface)',
                color: assetType === t ? 'var(--accent)' : 'var(--text-3)', fontWeight: assetType === t ? 600 : 400,
              }}>{t}</button>
            ))}
          </div>
        </Field>

        <Field label="Title *">
          <input value={title} onChange={e => setTitle(e.target.value)} style={inp} />
        </Field>

        <Field label={`Short Description (${shortDesc.length}/200)`}>
          <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)} rows={2} maxLength={200} style={{ ...inp, resize: 'vertical' }} />
        </Field>

        <Field label="Full Description (markdown)">
          <textarea value={fullDesc} onChange={e => setFullDesc(e.target.value)} rows={8} style={{ ...inp, resize: 'vertical', fontFamily: 'Space Mono', fontSize: 13 }} />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Category">
            <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select…</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="License">
            <select value={license} onChange={e => setLicense(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              {LICENSES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Price ($) — 0 for free">
          <input type="number" min="0" step="1" value={price} onChange={e => setPrice(e.target.value)} style={{ ...inp, maxWidth: 180 }} />
        </Field>

        <Field label="Tags" hint="Press Enter or comma to add">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', minHeight: 44 }}>
            {tags.map(t => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'var(--surface)', color: 'var(--text-3)', fontSize: 12 }}>
                {t}
                <button onClick={() => setTags(tags.filter(x => x !== t))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 0, fontSize: 14 }}>×</button>
              </span>
            ))}
            <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={addTag}
              placeholder="add tags…"
              style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 13, flexGrow: 1, minWidth: 100 }} />
          </div>
        </Field>
      </div>

      {/* ── Modality / Languages ─────────────────────────────────────────── */}
      {(features.modality || features.languages) && (
        <>
          <SectionTitle>Modality & Languages</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {features.modality && (
              <Field label="Data Modality">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {MODALITIES.map(m => {
                    const on = dataModality.includes(m);
                    return (
                      <button key={m} onClick={() => setDataModality(on ? dataModality.filter(x => x !== m) : [...dataModality, m])} style={{
                        padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', textTransform: 'capitalize',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                        background: on ? 'rgba(79,142,247,0.15)' : 'transparent',
                        color: on ? 'var(--accent)' : 'var(--text-3)',
                      }}>{m}</button>
                    );
                  })}
                </div>
              </Field>
            )}
            {features.languages && (
              <Field label="Languages">
                <LanguageSelect value={languages} onChange={setLanguages} />
              </Field>
            )}
          </div>
        </>
      )}

      {/* ── Model Metadata ───────────────────────────────────────────────── */}
      {features.modelMeta && (
        <>
          <SectionTitle>Model Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Architecture"><input value={modelMeta.architecture} onChange={e => setModelMeta({ ...modelMeta, architecture: e.target.value })} style={inp} /></Field>
            <Field label="Parameters"><input value={modelMeta.parameters} onChange={e => setModelMeta({ ...modelMeta, parameters: e.target.value })} style={inp} /></Field>
            <Field label="Framework">
              <select value={modelMeta.framework} onChange={e => setModelMeta({ ...modelMeta, framework: e.target.value })} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select…</option>
                {FRAMEWORKS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Task Type"><input value={modelMeta.taskType} onChange={e => setModelMeta({ ...modelMeta, taskType: e.target.value })} style={inp} /></Field>
            <Field label="Quantization">
              <select value={modelMeta.quantization} onChange={e => setModelMeta({ ...modelMeta, quantization: e.target.value })} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select…</option>
                {QUANTIZATIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </Field>
            <Field label="Base Model"><input value={modelMeta.baseModel} onChange={e => setModelMeta({ ...modelMeta, baseModel: e.target.value })} style={inp} /></Field>
          </div>
          {features.previewText && (
            <div style={{ marginTop: 12 }}>
              <Field label="Preview / Example Output">
                <textarea value={previewText} onChange={e => setPreviewText(e.target.value)} rows={4} style={{ ...inp, resize: 'vertical', fontFamily: 'Space Mono', fontSize: 12 }} />
              </Field>
            </div>
          )}
        </>
      )}

      {/* ── Dataset Metadata ─────────────────────────────────────────────── */}
      {features.datasetMeta && (
        <>
          <SectionTitle>Dataset Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Row Count">
              <input type="number" value={datasetMeta.rowCount} onChange={e => setDatasetMeta({ ...datasetMeta, rowCount: e.target.value })} style={inp} placeholder="e.g. 500000" />
            </Field>
            <Field label="Collection Method">
              <select value={datasetMeta.collectionMethod} onChange={e => setDatasetMeta({ ...datasetMeta, collectionMethod: e.target.value })} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select…</option>
                {COLLECTION_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Annotation Type">
              <select value={datasetMeta.annotationType} onChange={e => setDatasetMeta({ ...datasetMeta, annotationType: e.target.value })} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select…</option>
                {ANNOTATION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
          </div>
        </>
      )}

      {/* ── Cover Image ──────────────────────────────────────────────────── */}
      <SectionTitle>Cover Image</SectionTitle>
      <Field label="Image URL">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input value={previewImage} onChange={e => setPreviewImage(e.target.value)} style={{ ...inp, flex: 1 }} placeholder="https://…" />
          {previewImage && <img src={previewImage} alt="" style={{ height: 48, borderRadius: 6, border: '1px solid var(--border)' }} />}
        </div>
      </Field>

      {/* ── Files ────────────────────────────────────────────────────────── */}
      <SectionTitle>Files</SectionTitle>

      {/* Existing files */}
      {existingFiles.length > 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16, padding: '0 16px' }}>
          {existingFiles.map((f, i) => (
            <div key={String(f._id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < existingFiles.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontSize: 14, color: 'var(--text-3)' }}>📄</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{f.label || 'Untitled'}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'Space Mono' }}>
                  {f.format} {f.sizeBytes ? `· ${(f.sizeBytes / 1024 / 1024).toFixed(1)} MB` : ''} {f.isPrivate ? '· private' : '· public'}
                </div>
              </div>
              <button
                onClick={() => deleteExistingFile(String(f._id))}
                disabled={deletingFile === String(f._id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 18, padding: 4, opacity: deletingFile === String(f._id) ? 0.5 : 1 }}
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* Add new files */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={e => { e.preventDefault(); stageFiles(e.dataTransfer.files); }}
        onDragOver={e => e.preventDefault()}
        style={{ border: '2px dashed rgba(79,142,247,0.3)', borderRadius: 10, padding: 24, textAlign: 'center', cursor: 'pointer', marginBottom: 12, background: 'rgba(79,142,247,0.03)' }}
        onMouseOver={e => e.currentTarget.style.background = 'rgba(79,142,247,0.07)'}
        onMouseOut={e => e.currentTarget.style.background = 'rgba(79,142,247,0.03)'}
      >
        <div style={{ color: 'var(--text-2)', fontWeight: 500, fontSize: 13 }}>Browse or drag & drop to add files</div>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => stageFiles(e.target.files)} />
      </div>

      <button onClick={() => setAddLinkMode(!addLinkMode)} style={{ marginBottom: 12, padding: '7px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 13 }}>
        + Add external link
      </button>

      {addLinkMode && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Label</label>
                <input value={linkForm.label} onChange={e => setLinkForm({ ...linkForm, label: e.target.value })} placeholder="e.g. GitHub repo" style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Format</label>
                <input value={linkForm.format} onChange={e => setLinkForm({ ...linkForm, format: e.target.value })} placeholder="github / hf / zip" style={inp} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>URL *</label>
              <input value={linkForm.url} onChange={e => setLinkForm({ ...linkForm, url: e.target.value })} placeholder="https://…" style={inp} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text-3)' }}>
                <input type="checkbox" checked={linkForm.isPrivate} onChange={e => setLinkForm({ ...linkForm, isPrivate: e.target.checked })} />
                Private
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button onClick={addLink} style={{ padding: '7px 16px', borderRadius: 7, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Add</button>
                <button onClick={() => setAddLinkMode(false)} style={{ padding: '7px 12px', borderRadius: 7, background: 'transparent', color: 'var(--text-3)', border: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {newFiles.length > 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', padding: '0 16px', marginBottom: 16 }}>
          {newFiles.map((f, i) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < newFiles.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontSize: 14 }}>{f.isLink ? '🔗' : '📄'}</span>
              <div style={{ flex: 1 }}>
                <input value={f.label} onChange={e => setNewFiles(prev => prev.map(x => x.id === f.id ? { ...x, label: e.target.value } : x))}
                  style={{ ...inp, padding: '4px 8px', fontSize: 12 }} />
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'Space Mono', marginTop: 3 }}>
                  {f.isLink ? f.url : f.file?.name} · {f.format}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)', cursor: 'pointer' }}>
                <input type="checkbox" checked={f.isPrivate} onChange={e => setNewFiles(prev => prev.map(x => x.id === f.id ? { ...x, isPrivate: e.target.checked } : x))} />
                Private
              </label>
              <button onClick={() => setNewFiles(prev => prev.filter(x => x.id !== f.id))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 18, padding: 4 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Release version ──────────────────────────────────────────────── */}
      <div style={{ marginTop: 28, padding: '18px 20px', borderRadius: 10, border: `1.5px solid ${releaseVersion ? 'var(--accent)' : 'var(--border)'}`, background: releaseVersion ? 'var(--accent-bg)' : 'var(--surface)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={releaseVersion} onChange={e => setReleaseVersion(e.target.checked)} style={{ width: 16, height: 16 }} />
          <div>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Release as new version</span>
            <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 8 }}>
              v{originalAsset?.currentVersion || '1.0.0'} →{' '}
              v{bumpV(originalAsset?.currentVersion || '1.0.0')}
            </span>
          </div>
        </label>
        {releaseVersion && (
          <textarea
            value={changelog} onChange={e => setChangelog(e.target.value)}
            placeholder="What changed in this version? (добавили 50k строк, поправили разметку…)"
            rows={3}
            style={{ ...inp, marginTop: 12, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
          />
        )}
      </div>

      {/* ── Action buttons ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
        <button onClick={() => navigate('/dashboard/assets')} style={{ padding: '12px 20px', borderRadius: 8, background: 'transparent', color: 'var(--text-3)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '12px', borderRadius: 8, background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
          {saving ? (uploadProgress || 'Saving…') : 'Save Changes'}
        </button>
        {!isPublished && (
          <button onClick={handlePublish} disabled={saving || publishing} style={{ flex: 1, padding: '12px', borderRadius: 8, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, opacity: (saving || publishing) ? 0.7 : 1 }}>
            {publishing ? 'Publishing…' : 'Save & Publish →'}
          </button>
        )}
      </div>
    </div>
  );
}
